import { useMemo, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useReservationTypes } from '../../../hooks/AppData/useReservationTypes';
import { ALL_FEATURES } from '../../../config/features';
import { RESERVATION_STATUSES, Reservation } from '../../../types/reservation';
import { hasFeaturesPermission } from '../../../utils/featureUtils';
import { isPastReservation } from '../../../utils/reservationUtils';
import { ClientType, ReservationEvent, ReservationWithCancellation } from '../types';
import { getInitialClientType } from '../utils/form';
import { getHourPickerEventTitle } from '../utils/slots';

interface Options {
  resource: Reservation | undefined;
  isEditing: boolean;
  createModeReservationTypeId: string | undefined;
  initialTempEventOverride: ReservationEvent | undefined;
}

/** What the panel knows about the reservation before the form: its type, its state in time and what the user may do. */
export const useReservationState = ({ resource, isEditing, createModeReservationTypeId, initialTempEventOverride }: Options) => {
  const { features, featuresLoading } = useAuthContext();
  const isCreating = !resource;
  const isModifying = isCreating || isEditing;

  const [selectedClientType, setSelectedClientType] = useState<ClientType>();
  const initialClientType = useMemo(() => getInitialClientType(resource), [resource]);
  const clientType = selectedClientType ?? initialClientType;

  const { reservationTypeFromId, findReservationTypeByBranchAndName } = useReservationTypes();
  const reservationType = useMemo(
    () => reservationTypeFromId((isCreating ? createModeReservationTypeId : resource?.reservationTypeId) ?? ''),
    [createModeReservationTypeId, isCreating, reservationTypeFromId, resource?.reservationTypeId],
  );
  const isGroupReservation = !!reservationType && reservationType.groupSize > 1;
  const isUnregisteredClient = !!reservationType?.hasUnregisteredClients && clientType === 'unregistered';

  const userHasWriteFeatures = reservationType?.features.manage
    ? hasFeaturesPermission([reservationType?.features.manage], { features, featuresLoading })
    : false;

  const hasRoutinesPermission = hasFeaturesPermission([ALL_FEATURES.ROUTINES], { features, featuresLoading });

  const initialTemporaryEvent = useMemo(() => {
    if (!isEditing || !resource) return initialTempEventOverride;
    const start = new Date(resource.localStartDate);
    start.setMilliseconds(0);
    const end = new Date(resource.localEndDate);
    end.setMilliseconds(0);
    return { start, end, title: getHourPickerEventTitle(resource, reservationType, !isEditing), id: 'temporary' };
  }, [isEditing, resource, initialTempEventOverride, reservationType]);

  const isPast = useMemo(() => (resource ? isPastReservation(resource) : false), [resource]);
  // True once the reservation has started: its clients can no longer be removed and attendance can be registered.
  const hasStarted = isPast || (!!resource && new Date(resource.localStartDate) <= new Date());

  const isCanceled =
    (resource as ReservationWithCancellation)?.status === RESERVATION_STATUSES.CANCELED ||
    (resource as ReservationWithCancellation)?.externalUserStatus === RESERVATION_STATUSES.CANCELED;

  return {
    isCreating,
    isModifying,
    clientType,
    setSelectedClientType,
    reservationType,
    reservationTypeFromId,
    findReservationTypeByBranchAndName,
    isGroupReservation,
    isUnregisteredClient,
    userHasWriteFeatures,
    hasRoutinesPermission,
    initialTemporaryEvent,
    isPast,
    hasStarted,
    isCanceled,
  };
};
